import React, {
  useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import _ from 'lodash';
import classes from './styles.module.scss';
import SelectOptions from './SelectOptions';
import { ReactComponent as ClearSvg } from '../../../../../assets/icons/close.svg';

let optionInfo = {
  position: 0,
  findIndexes: [],
};

const Select = ({
  options = [], currentOption = '', onChange, label = '', wrapperClassName = '', height = 50, labelDefaultPosition = 12,
  optionWrapperHeight = 209, clearBtnClassName = '', nextOptionListRequest = () => {}, totalPage = 0, currentPage = 0,
  limit = 0, zIndex = 11, hideClearButton = false, arrowActiveClassName = '', labelBG = '#FFFFFF', error = '',
  errorClassName = '', optionPathname = '', optionRenderPath = '', closeOnScroll = false, placeholder = '',
  arrowTop = 19, optionValue = false, optionsClassName = '', disabled,
}) => {
  const uniqId = useId();
  const selectRef = useRef();
  const wrapRef = useRef(null);

  const [optionsPosition, setOptionsPosition] = useState({});
  const [showOptions, showOptionsToggle] = useState(false);
  const [activeSelect, setActiveSelect] = useState(false);
  const [optionsOpacity, setOptionsOpacity] = useState(false);
  const [hasNextPage, hasNextPageToggle] = useState(false);

  const [pageLoading, pageLoadingToggle] = useState(false);

  const closeMenu = (e) => {
    if (!e.target.closest(`[id="select_container${uniqId}"]`)
      && !e.target.closest(`[id="option_container${uniqId}"]`)) {
      showOptionsToggle(false);
      setActiveSelect(false);
    }
  };

  const setSelectOptionsPosition = (e) => {
    if (closeOnScroll && e?.type === 'scroll') {
      showOptionsToggle(false);
    } else {
      const selectorRef = document.getElementById(`select_container${uniqId}`);

      const optionsRef = document.getElementById(`option_wrapper${uniqId}`);

      const optionsHeight = optionsRef?.getBoundingClientRect()?.height || 0;

      const { left, top } = selectorRef?.getBoundingClientRect() || { left: 0, top: 0 };

      const topPosition = top + window.scrollY + height;

      const bottomPosition = top + window.scrollY - (optionsHeight + 8);

      const showInTop = window.innerHeight + window.scrollY > topPosition + optionsHeight;
      setOptionsPosition({ left, top: showInTop ? topPosition : bottomPosition });
    }
  };

  useEffect(() => {
    window.addEventListener('click', closeMenu, true);
    window.addEventListener('scroll', setSelectOptionsPosition, true);
    window.addEventListener('resize', setSelectOptionsPosition, true);

    return () => {
      window.removeEventListener('click', closeMenu, true);
      window.removeEventListener('scroll', setSelectOptionsPosition, true);
      window.addEventListener('resize', setSelectOptionsPosition, true);
    };
  }, []);

  const keyPress = async (e) => {
    if (e.target?.id === `select_container${uniqId}` && e.key !== 'Tab') {
      if (e.key === 'Enter') {
        showOptionsToggle((prev) => !prev);
        return;
      }

      document.body.style.width = `${document.body.getBoundingClientRect().width}px`;
      const findIndexes = [];

      options.forEach((o, i) => {
        const option = optionPathname
          ? o[optionPathname]
          : o;

        if (typeof option !== 'string'
          ? option?.name?.toLowerCase()?.startsWith(e.key)
          : option?.toLowerCase()?.startsWith(e.key)) {
          findIndexes.push(i);
        }
      });

      const allElements = document.querySelectorAll('[class^="option_id_"]');
      allElements.forEach((el) => el.style.color = '#1D2740');

      if (!_.isEmpty(findIndexes)) {
        optionInfo.position = _.isEqual(optionInfo.findIndexes, findIndexes)
        && findIndexes.length - 1 !== optionInfo.position ? optionInfo.position + 1 : 0;
        optionInfo.findIndexes = findIndexes;

        await wrapRef.current?._listRef?.scrollToItem(findIndexes[optionInfo.position]);

        const option = optionPathname
          ? options[findIndexes[optionInfo.position]][optionPathname]
          : options[findIndexes[optionInfo.position]];

        onChange(option);

        const element = document.querySelector(`[class^="option_id_${findIndexes[optionInfo.position]}"]`);

        if (element) element.style.color = '#355ECA';
      } else {
        if (!hideClearButton) {
          onChange('');
        }

        optionInfo = {
          position: 0,
          findIndexes: [],
        };
      }
    }
  };

  useEffect(() => {
    if (showOptions || activeSelect) {
      window.addEventListener('keyup', keyPress, true);
    }

    return () => {
      window.removeEventListener('keyup', keyPress, true);
    };
  }, [showOptions, activeSelect]);

  useEffect(() => {
    if (showOptions) {
      setSelectOptionsPosition();
      setTimeout(() => setOptionsOpacity(true));
    } else {
      setOptionsOpacity(false);
    }
  }, [showOptions]);

  const selectOption = (option) => {
    onChange(optionPathname && !optionValue ? option[optionPathname] : option);
    showOptionsToggle(false);
  };

  const clearOption = () => {
    showOptionsToggle(false);
    setActiveSelect(false);
    onChange('');
  };

  useEffect(() => {
    if (currentPage) {
      hasNextPageToggle(totalPage > currentPage || !totalPage);
    }
  }, [currentPage, totalPage]);

  const nextPageRequest = async (...params) => {
    pageLoadingToggle(true);
    await nextOptionListRequest(Math.ceil(params[0] / limit) + 1);
    pageLoadingToggle(false);
  };

  const wrapperCls = [classes.select_wrapper, wrapperClassName];

  if (disabled) wrapperCls.push(classes.disabled);
  if (error) wrapperCls.push(classes.error);

  const arrowCls = [classes.arrow];

  const clearCls = [classes.clear_svg, clearBtnClassName];

  const selectCls = [classes.select_title];
  if (showOptions || activeSelect) {
    selectCls.push(classes.active);

    if (showOptions) arrowCls.push(classes.arrow_up);

    if (arrowActiveClassName) arrowCls.push(arrowActiveClassName);
  }

  if (currentOption || placeholder) selectCls.push(classes.with_value);

  const optionsCls = [classes.options_wrapper, optionsClassName];

  if (optionsOpacity) optionsCls.push(classes.show);

  const { left, top = 0 } = optionsPosition || { left: 0, top: 0 };

  const blur = () => {
    if (showOptions) {
      setActiveSelect(false);
      setTimeout(() => showOptionsToggle(false), 200);
    }
  };

  return (
    <>
      <div
        style={{ height }}
        className={wrapperCls.join(' ')}
        tabIndex={!disabled ? 0 : undefined}
        id={`select_container${uniqId}`}
        onFocus={() => !disabled && setActiveSelect(true)}
        onBlur={blur}
      >
        <div
          role="button"
          tabIndex="0"
          ref={selectRef}
          className={selectCls.join(' ')}
          onClick={() => !disabled && showOptionsToggle((prev) => !prev)}
        >
          {currentOption || placeholder}
        </div>

        <div
          style={{ top: arrowTop + (showOptions ? 2 : 0) }}
          className={arrowCls.join(' ')}
        />

        {currentOption && !hideClearButton && (
          <ClearSvg
            className={clearCls.join(' ')}
            onClick={clearOption}
          />
        )}

        {label
          && (
            <label
              style={{
                '--default-position': `${labelDefaultPosition}px`,
                '--label-bg': `${labelBG}`,
              }}
            >
              {label}
            </label>
          )}

        {!!error && typeof error === 'string' && <p className={`${classes.error_text} ${errorClassName}`}>{error}</p>}
      </div>

      {showOptions && createPortal(
        <div
          id={`option_container${uniqId}`}
          style={{
            zIndex, left, top, width: selectRef?.current?.offsetWidth,
          }}
          className={optionsCls.join(' ')}
        >
          <SelectOptions
            hasNextPage={hasNextPage}
            isNextPageLoading={pageLoading}
            items={options}
            optionWrapperHeight={optionWrapperHeight}
            optionPathname={optionRenderPath || optionPathname || ''}
            width={selectRef?.current?.offsetWidth}
            loadNextPage={nextPageRequest}
            selectOption={selectOption}
            uniqId={uniqId}
            innerRef={wrapRef}
          />
        </div>,
        document.getElementById('root') || document.body,
      )}
    </>
  );
};

Select.propTypes = {
  currentOption: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  options: PropTypes.array,
  optionPathname: PropTypes.string,
  optionRenderPath: PropTypes.string,
  wrapperClassName: PropTypes.string,
  optionsClassName: PropTypes.string,
  clearBtnClassName: PropTypes.string,
  label: PropTypes.string,
  height: PropTypes.number,
  nextOptionListRequest: PropTypes.func,
  totalPage: PropTypes.number,
  currentPage: PropTypes.number,
  limit: PropTypes.number,
  labelDefaultPosition: PropTypes.number,
  arrowTop: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.any,
  labelBG: PropTypes.string,
  arrowActiveClassName: PropTypes.string,
  errorClassName: PropTypes.string,
  zIndex: PropTypes.number,
  placeholder: PropTypes.string,
  closeOnScroll: PropTypes.bool,
  hideClearButton: PropTypes.bool,
  optionValue: PropTypes.bool,
  optionWrapperHeight: PropTypes.number,
  disabled: PropTypes.bool,
};

export default Select;
